import { createSlice } from '@reduxjs/toolkit';

/**
 * @typedef {'idle' | 'fetchingToken' | 'connecting' | 'ringing' | 'inCall' | 'ended' | 'remoteEnded' | 'error'} CallStatus
 */

/**
 * @typedef {{ deviceId: string, label: string }} AudioDeviceInfo
 *
 * @typedef {Object} AudioState
 * @property {AudioDeviceInfo[]} inputDevices
 * @property {AudioDeviceInfo[]} outputDevices
 * @property {string | null} selectedInputDeviceId  null = browser/OS default
 * @property {string | null} selectedOutputDeviceId
 * @property {boolean} outputSelectionSupported     true if HTMLMediaElement.setSinkId exists in this browser
 *
 * @typedef {Object} PatientSnapshot
 * @property {string} [firstName]
 * @property {string} [lastName]
 * @property {string} [phoneNumber]
 *
 * @typedef {Object} TwilioCallState
 * @property {CallStatus} status
 * @property {string | null} callSid
 * @property {number | null} clinicId
 * @property {number | null} patientId
 * @property {PatientSnapshot | null} patientSnapshot
 * @property {string | null} toNumber
 * @property {number | null} startedAt   epoch ms when call was answered
 * @property {boolean} muted
 * @property {string | null} error
 * @property {string | null} terminalMessage  human-readable label for remoteEnded state (e.g. 'Patient hung up')
 * @property {AudioState} audio
 *
 * @typedef {null | 'PENDING' | 'STORED' | 'FAILED'} TranscriptStatus
 * @typedef {null | 'PENDING' | 'GENERATING' | 'READY' | 'FAILED' | 'SKIPPED_SHORT_TRANSCRIPT'} NoteStatus
 *
 * @typedef {Object} GeneratedNoteState
 * @property {TranscriptStatus} transcriptStatus  null = idle (no poll in flight)
 * @property {NoteStatus} noteStatus              null = idle
 * @property {string | null} text                 the Bedrock-generated note, populated when noteStatus === 'READY'
 */

// status machine for the active outbound call.
//   idle         → no call in flight
//   fetchingToken→ access-token request in flight
//   connecting   → Device.connect() called, waiting for Twilio
//   ringing      → Twilio is dialing the destination
//   inCall       → call answered, audio bridged
//   ended        → terminal, agent-initiated hangup — card vanishes
//   remoteEnded  → terminal, remote disconnect (patient hung up / no answer /
//                  drop); card lingers with terminalMessage until dismissed
//   error        → terminal failure, surfaced in card
// Audio device sub-state. Lives on the call slice but is meaningful only while
// a call is active — the picker is mounted on the floating call card and the
// device list is sourced from `device.audio` once the Twilio Device is built.
// Persistence of the *preferred* deviceIds across calls is handled in
// localStorage by the controller, not here.
const initialAudioState = {
    inputDevices: [], // [{ deviceId, label }] — `label` may be '' until mic permission is granted
    outputDevices: [],
    selectedInputDeviceId: null, // null = browser/OS default
    selectedOutputDeviceId: null,
    outputSelectionSupported: false // gets flipped to true at startCall when HTMLMediaElement.setSinkId is present
    // inputVolume is intentionally NOT on the slice — the picker polls
    // device.audio.inputVolume via twilioCallController.getInputVolume() into
    // component-local state to avoid 20Hz Redux dispatches.
};

/** @type {TwilioCallState} */
const initialState = {
    status: 'idle',
    callSid: null,
    clinicId: null, // captured at startCall so the card can route back to the patient page after navigation
    patientId: null,
    patientSnapshot: null, // { firstName, lastName, phoneNumber } — written on startCall so the card survives navigation
    toNumber: null,
    startedAt: null,
    muted: false,
    error: null,
    terminalMessage: null,
    audio: initialAudioState,
    // Post-call transcript→note pipeline state. Survives callCardDismissed
    // (the pipeline outlives the card); wiped by cancelGeneratedNotePolling
    // on patient-page unmount, or by generatedNoteCleared after the note is
    // appended. Both statuses null = idle.
    /** @type {GeneratedNoteState} */
    generatedNote: {
        transcriptStatus: null,
        noteStatus: null,
        text: null
    }
};

const slice = createSlice({
    name: 'twilioOutboundCall',
    initialState,
    reducers: {
        callFetchingToken(state, action) {
            const { clinicId, patientId, patientSnapshot, toNumber } = action.payload;
            state.status = 'fetchingToken';
            state.clinicId = clinicId;
            state.patientId = patientId;
            state.patientSnapshot = patientSnapshot;
            state.toNumber = toNumber;
            state.callSid = null;
            state.startedAt = null;
            state.muted = false;
            state.error = null;
            state.terminalMessage = null;
        },
        callConnecting(state) {
            state.status = 'connecting';
        },
        callRinging(state, action) {
            state.status = 'ringing';
            state.callSid = action.payload?.callSid ?? state.callSid;
        },
        callInProgress(state, action) {
            state.status = 'inCall';
            state.startedAt = action.payload?.startedAt ?? Date.now();
            state.callSid = action.payload?.callSid ?? state.callSid;
        },
        callEnded(state) {
            state.status = 'ended';
        },
        callRemoteDisconnect(state, action) {
            state.status = 'remoteEnded';
            state.terminalMessage = action.payload?.message ?? 'Call ended';
        },
        callError(state, action) {
            state.status = 'error';
            state.error = action.payload ?? 'Call failed';
        },
        setMuted(state, action) {
            state.muted = !!action.payload;
        },
        setAudioDevices(state, action) {
            const { inputDevices, outputDevices } = action.payload;
            state.audio.inputDevices = inputDevices ?? [];
            state.audio.outputDevices = outputDevices ?? [];
        },
        setSelectedInputDeviceId(state, action) {
            state.audio.selectedInputDeviceId = action.payload ?? null;
        },
        setSelectedOutputDeviceId(state, action) {
            state.audio.selectedOutputDeviceId = action.payload ?? null;
        },
        setOutputSelectionSupported(state, action) {
            state.audio.outputSelectionSupported = !!action.payload;
        },
        generatedNoteUpdated(state, action) {
            const { transcriptStatus, noteStatus, text } = action.payload ?? {};
            state.generatedNote.transcriptStatus = transcriptStatus ?? null;
            state.generatedNote.noteStatus = noteStatus ?? null;
            state.generatedNote.text = text ?? null;
        },
        generatedNoteCleared(state) {
            state.generatedNote.transcriptStatus = null;
            state.generatedNote.noteStatus = null;
            state.generatedNote.text = null;
        },
        callCardDismissed(state) {
            // Resets call-lifecycle fields; preserves generatedNote (the
            // pipeline outlives the card). Pair with generatedNoteCleared to
            // wipe the note half.
            return { ...initialState, generatedNote: state.generatedNote };
        }
    }
});

export default slice.reducer;

export const {
    callFetchingToken,
    callConnecting,
    callRinging,
    callInProgress,
    callEnded,
    callRemoteDisconnect,
    callError,
    setMuted,
    setAudioDevices,
    setSelectedInputDeviceId,
    setSelectedOutputDeviceId,
    setOutputSelectionSupported,
    generatedNoteUpdated,
    generatedNoteCleared,
    callCardDismissed
} = slice.actions;

// "Active" = a call is in flight that the chrome must protect. Excludes terminal
// states (idle, ended, remoteEnded, error) — those are terminal; the floating
// card may still render a message but no call is in progress, so nav should
// not be locked.
/**
 * @param {CallStatus} status
 * @returns {boolean}
 */
export function isCallActiveStatus(status) {
    return status !== 'idle' && status !== 'ended' && status !== 'remoteEnded' && status !== 'error';
}
